import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { DYNAMIC_TASKS_CONFIG, TASKS_CONFIGS } from '../../../constants';
import type { Task, TaskShape } from '../../../types';

import { useDescription } from './useDescription';
import { useGetAction } from './useGetAction';
import { useGetHref } from './useGetHref';
import { useTitle } from './useTitle';

type UseTasksReturn = {
	tasks: TaskShape[];
	stats: {
		completed: number;
		count: number;
	};
};

// validates task metadata for dynamic tasks
function hasValidMetadata(task: Task): boolean {
	switch (task.name) {
		case 'share_top_starter_share_channel':
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			if (!task.metadata || !Object.hasOwn(task.metadata, 'channel')) return false;
			// translations are present for channel
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
			if (!Object.hasOwn(DYNAMIC_TASKS_CONFIG.share_top_starter_share_channel.channel, task.metadata.channel))
				return false;
			break;
		default:
			break;
	}
	return true;
}

export function useTasks(): UseTasksReturn {
	const { typedTranslate } = useI18n();
	const { data } = useStarterDashboardV2PageData();
	const getHref = useGetHref();
	const getAction = useGetAction();
	const getTitle = useTitle();
	const getDescription = useDescription();

	// combine active + completed (within last 24hrs) tasks
	const combinedTasks = useMemo(() => {
		if (
			data.petition.activeTasks.__typename !== 'PetitionTasksConnection' ||
			data.petition.completedTasks.__typename !== 'PetitionTasksConnection'
		) {
			return { tasks: [], stats: { count: 0, completed: 0 } };
		}

		const tasks = [...data.petition.activeTasks.nodes, ...data.petition.completedTasks.nodes];
		return {
			tasks,
			stats: {
				count: data.petition.activeTasks.totalCount + data.petition.completedTasks.totalCount,
				completed: data.petition.completedTasks.totalCount,
			},
		};
	}, [data.petition.activeTasks, data.petition.completedTasks]);

	const tasks = combinedTasks.tasks
		.filter((task) => Object.hasOwn(TASKS_CONFIGS, task.name) && hasValidMetadata(task))
		.map((task) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const key = task.name as keyof typeof TASKS_CONFIGS;

			return {
				...task,
				icon: TASKS_CONFIGS[key].icon,
				primaryActionText: typedTranslate(TASKS_CONFIGS[key].primaryActionText),
				// dynamic properties
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				title: getTitle({ name: task.name, metadata: task.metadata }),
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				description: getDescription({ name: task.name, metadata: task.metadata }),
				primaryActionHref: getHref({ name: task.name }),
				primaryActionOnClick: getAction({ id: task.id, name: task.name }),
			};
		});

	const stats = {
		completed: tasks.filter((task) => task.status === 'COMPLETED').length,
		count: tasks.length,
	};

	return {
		tasks,
		stats,
	};
}
